// O custo real de um funcionário CLT numa empresa do Simples Nacional. Puro,
// com prova própria (`app/tests/simples-nacional.mjs`).
//
// ---------------------------------------------------------------------------
// POR QUE ISTO EXISTE SE O HUB DP JÁ TEM ENCARGOS
// ---------------------------------------------------------------------------
// `encargosDp.calcularEncargos` responde "quanto a empresa paga de encargo neste
// mês, na folha ou na CPRB". A pergunta aqui é outra: "quanto custa CONTRATAR
// esta pessoa", e ela soma provisões (13º, férias, multa do FGTS) que não são
// desembolso do mês — são reserva —, mais SST e benefícios.
//
// O que NÃO se repete são os números. `INSS_PATRONAL` e `ALIQUOTA_FGTS` vêm dos
// módulos do HUB DP; escrever `0.2` e `0.08` de novo criaria duas versões de uma
// constante da Lei 8.212, e a que divergisse em silêncio seria a que ninguém lê.
//
// ---------------------------------------------------------------------------
// A REGRA QUE MUDA TUDO: NOS ANEXOS III E V, A CPP ESTÁ DENTRO DO DAS
// ---------------------------------------------------------------------------
// A empresa do Simples enquadrada nos Anexos I, II, III e V **não recolhe** a
// contribuição patronal de 20%, nem o RAT, nem as contribuições a terceiros:
// tudo isso já está embutido no percentual do DAS (LC 123/2006, art. 13, VI, e
// §3º do art. 13 para os terceiros).
//
// O Anexo IV é a exceção nomeada na própria lei (art. 18, §5º-C): ali a CPP sai
// do DAS e a empresa recolhe em guia própria. É por isso que o mesmo salário
// custa cerca de 26 pontos percentuais a mais no Anexo IV.
//
// Note a diferença para a CPRB do HUB DP, que é outro regime: lá a desoneração
// substitui APENAS os 20% patronais e o RAT/terceiros continuam sobre a folha —
// e o comentário de `encargosDp` diz isso com todas as letras. Aqui os três
// saem juntos. Calcular um pelo outro erraria nos dois sentidos.
//
// ---------------------------------------------------------------------------
// PROVISÃO NÃO É ENCARGO, E A TELA PRECISA SEPARAR
// ---------------------------------------------------------------------------
// FGTS é desembolso todo mês. 13º, férias e multa rescisória são RESERVA: o
// dinheiro sai depois (ou não sai, se a pessoa pedir demissão). Somá-los na
// mesma linha de "encargos" dá um percentual que não corresponde a nenhum
// pagamento — e é o número que o contador leva para o cliente.

import { INSS_PATRONAL } from "./encargosDp";
import { ALIQUOTA_FGTS, centavos } from "./folhaDp";

// ---------------------------------------------------------------------------
// As provisões — percentuais estruturais da CLT e da Constituição
// ---------------------------------------------------------------------------

/** 13º salário: um salário ao ano, provisionado em 12 avos (CF art. 7º, VIII). */
export const PROVISAO_13 = 1 / 12;

/**
 * Férias + 1/3 constitucional (CF art. 7º, XVII): (1 + 1/3) ÷ 12.
 *
 * Dá 0,111111…, que a origem escrevia como `0.1111` — quatro casas. Num salário
 * de R$ 5.000 a diferença é de R$ 0,06 por mês; em doze meses e dez
 * funcionários, R$ 6,66. Pequeno, e não há razão para arredondar uma fração
 * exata: aqui ela é a fração.
 */
export const PROVISAO_FERIAS = (1 + 1 / 3) / 12;

/**
 * Multa rescisória do FGTS: 40% sobre o saldo depositado (Lei 8.036/1990,
 * art. 18, §1º), provisionada mês a mês sobre os 8% que entram — 8% × 40%.
 *
 * É DERIVADA, não uma constante de 3,2%: se a alíquota do FGTS mudar, este
 * número acompanha sozinho. O que dá para derivar não se guarda.
 */
export const MULTA_FGTS_PERCENTUAL = 0.4;

/**
 * A contribuição a terceiros (SESI/SENAI/SEBRAE/INCRA/salário-educação) mais
 * comum, em percentual. Só incide no Anexo IV.
 *
 * É o padrão do FPAS 507 (indústria/comércio) e o valor que a origem usava. Fica
 * EDITÁVEL na tela porque varia por FPAS — e o CNAE escolhido traz o FPAS dele,
 * então a tela pode sugerir sem cravar.
 */
export const TERCEIROS_PADRAO = 5.8;

export interface EntradaCusto {
  salario: number;
  /** 1 a 5. Só o 4 tira a CPP do DAS. */
  anexo: number;
  /** Grau de risco em percentual: 1, 2 ou 3. */
  ratPercentual: number;
  terceirosPercentual: number;
  /** Custo mensal de SST (PGR/PCMSO), quando a empresa contrata fora. */
  sst: number;
  valeTransporte: number;
  valeAlimentacao: number;
  planoSaude: number;
  outrosBeneficios: number;
}

export interface ResultadoCusto {
  salario: number;
  anexo: number;
  /** True só no Anexo IV: ali a CPP, o RAT e os terceiros saem do DAS. */
  patronalForaDoDas: boolean;

  inssPatronal: number;
  rat: number;
  terceiros: number;
  fgts: number;
  /** Desembolso do mês, além do salário. */
  encargos: number;

  provisao13: number;
  provisaoFerias: number;
  provisaoMultaFgts: number;
  /** Reserva do mês — sai do caixa depois, ou não sai. */
  provisoes: number;

  sst: number;
  beneficios: number;

  custoTotal: number;
  /** encargos ÷ salário, em percentual. */
  encargosPercentual: number;
  /** provisões ÷ salário, em percentual. */
  provisoesPercentual: number;
  /** custo total ÷ salário, em percentual. */
  custoPercentual: number;

  /**
   * Quanto o Anexo IV custa a mais que os demais, para o MESMO salário. Nulo
   * quando o anexo já é o IV.
   *
   * A pergunta que o contador faz ao escolher o anexo é essa, e a origem não
   * respondia: ela mostrava as linhas zeradas com a etiqueta "Incluso no DAS",
   * o que informa que não paga, mas não quanto isso vale.
   */
  quantoOAnexoIvCustariaAMais: number | null;
}

export function calcularCusto(e: EntradaCusto): ResultadoCusto {
  const salario = Math.max(0, e.salario);
  const patronalForaDoDas = e.anexo === 4;

  const rat = Math.max(0, e.ratPercentual) / 100;
  const terceiros = Math.max(0, e.terceirosPercentual) / 100;

  const inssPatronal = patronalForaDoDas ? centavos(salario * INSS_PATRONAL) : 0;
  const ratValor = patronalForaDoDas ? centavos(salario * rat) : 0;
  const terceirosValor = patronalForaDoDas ? centavos(salario * terceiros) : 0;
  // O FGTS incide em TODOS os anexos: ele não é contribuição previdenciária e o
  // Simples não o substitui (LC 123/2006, art. 13, §1º, VIII).
  const fgts = centavos(salario * ALIQUOTA_FGTS);

  const encargos = centavos(inssPatronal + ratValor + terceirosValor + fgts);

  const provisao13 = centavos(salario * PROVISAO_13);
  const provisaoFerias = centavos(salario * PROVISAO_FERIAS);
  const provisaoMultaFgts = centavos(salario * ALIQUOTA_FGTS * MULTA_FGTS_PERCENTUAL);
  const provisoes = centavos(provisao13 + provisaoFerias + provisaoMultaFgts);

  const sst = Math.max(0, e.sst);
  const beneficios = centavos(
    Math.max(0, e.valeTransporte) +
      Math.max(0, e.valeAlimentacao) +
      Math.max(0, e.planoSaude) +
      Math.max(0, e.outrosBeneficios),
  );

  const custoTotal = centavos(salario + encargos + provisoes + sst + beneficios);
  const pct = (v: number) => (salario > 0 ? Math.round((v / salario) * 10000) / 100 : 0);

  return {
    salario,
    anexo: e.anexo,
    patronalForaDoDas,
    inssPatronal,
    rat: ratValor,
    terceiros: terceirosValor,
    fgts,
    encargos,
    provisao13,
    provisaoFerias,
    provisaoMultaFgts,
    provisoes,
    sst,
    beneficios,
    custoTotal,
    encargosPercentual: pct(encargos),
    provisoesPercentual: pct(provisoes),
    custoPercentual: pct(custoTotal),
    quantoOAnexoIvCustariaAMais: patronalForaDoDas
      ? null
      : centavos(salario * (INSS_PATRONAL + rat + terceiros)),
  };
}
